import { useState, useEffect, useRef } from 'react';
import { useOpenAiCompletion, COMPLETION_STATUS } from '@client/hooks/useOpenAiCompletion';
import { useConcurrentSearch } from '@client/hooks/useConcurrentSearch';
import { Button } from '@/components/ui/button';
import { SheetModal } from '@/components/SheetModal';
import { Loader2, Sparkles, CircleX } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import {
  postAiTypeformPayloadByClientIdRequest,
  resetAiUnderwritingTypeformState,
} from '@client/model/aiUnderwritingTypeformModel';
import { selectAiUnderwritingTypeform, selectMaybeClientPartner } from '@client/model/selectors';
import {
  FundingPolicyType,
  logInfo,
  parseUnderwritingGuidanceResponse,
  policyStepConfigs,
} from '@shared';
import { AiUnderwritingGuidanceResultComponent } from '@client/components/AiUnderwritingGuidanceResultComponent';

interface AiUnderwritingGuidanceProps {
  clientId: string;
  formData: PolicyFormSubmissionData;
  buttonText?: string;
  buttonVariant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  buttonSize?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
  disableAbsolutePosition?: boolean;
  forceRefresh?: boolean;
}

export function AiUnderwritingGuidance({
  clientId,
  formData,
  buttonText = 'Initial Insights',
  buttonVariant = 'secondary',
  buttonSize = 'default',
  className = '',
  disableAbsolutePosition = false,
  forceRefresh = false,
}: AiUnderwritingGuidanceProps) {
  const dispatch = useDispatch();
  const aiUnderwritingTypeform = useSelector(selectAiUnderwritingTypeform);
  const typeformPayload = aiUnderwritingTypeform.data.client;
  const isTypeformLoading = aiUnderwritingTypeform.isLoading.client;
  const typeformError = aiUnderwritingTypeform.error.client;
  const partnerTypeformPayload = aiUnderwritingTypeform.data.partner;
  const isPartnerTypeformLoading = aiUnderwritingTypeform.isLoading.partner;
  const clientPartner = useSelector(selectMaybeClientPartner);

  const { createCompletion, status, result, error } = useOpenAiCompletion({
    pollingInterval: 4000,
    maxPollingAttempts: 80,
  });
  const concurrentSearch = useConcurrentSearch('ai_underwriting_guidance');
  const [isOpen, setIsOpen] = useState(false);
  const [hasFetchedTypeform, setHasFetchedTypeform] = useState(false);
  const [searchResult, setSearchResult] = useState<OpenAiCompletionDbRecord | null>(null);
  const [shouldForceRefresh, setShouldForceRefresh] = useState(false);
  const previousFormDataRef = useRef<PolicyFormSubmissionData | null>(null);

  // Detect formData changes and set force refresh flag
  useEffect(() => {
    const previousFormData = previousFormDataRef.current;
    const currentFormData = formData;

    // On first render, store the initial formData
    if (previousFormData === null) {
      previousFormDataRef.current = currentFormData;
      setShouldForceRefresh(false);
      return;
    }

    // Compare form data to detect meaningful changes (ignoring timestamps, etc.)
    const formDataChanged = JSON.stringify(previousFormData) !== JSON.stringify(currentFormData);

    if (formDataChanged) {
      logInfo('AiUnderwritingGuidance', 'Form data changed, will force refresh on next analysis', {
        previousFormData: previousFormData,
        currentFormData: currentFormData,
      });
      setShouldForceRefresh(true);
      previousFormDataRef.current = currentFormData;
    }
  }, [formData]);

  const handleGetUnderwritingGuidance = () => {
    if (!clientId) {
      logInfo('AiUnderwritingGuidance', 'No client data available for underwriting analysis', {});
      return;
    }

    logInfo('AiUnderwritingGuidance', 'Starting AI underwriting guidance', {
      policyType: formData?.policyType,
    });

    // Check if formData is empty or minimal, and we have typeform data
    const isFormDataEmpty = !formData || Object.keys(formData).length <= 3;
    let dataToUse =
      isFormDataEmpty && typeformPayload?.typeformPayload
        ? { ...formData, clientData: typeformPayload.typeformPayload }
        : formData;

    // For joint policies, include partner intake data if available
    const isJointPolicy =
      formData?.policyType && policyStepConfigs[formData.policyType as FundingPolicyType]?.isJoint;
    if (isJointPolicy) {
      if (partnerTypeformPayload?.typeformPayload) {
        logInfo('AiUnderwritingGuidance', 'Including partner intake data in AI analysis', {
          hasPartnerData: true,
          partnerClientId: clientPartner?.clientId,
        });
        dataToUse = {
          ...dataToUse,
          partnerData: partnerTypeformPayload.typeformPayload,
        };
      } else if (clientPartner?.clientId) {
        logInfo('AiUnderwritingGuidance', 'Partner exists but no typeform data received', {
          partnerClientId: clientPartner.clientId,
        });
      }
    }

    // Determine policyType based on formData.policyType
    let aiPromptType = 'underwriting-nw-guidance'; // default
    switch (formData?.policyType) {
      case FundingPolicyType.careMattersTwo:
        aiPromptType = 'underwriting-nw-guidance';
        break;
      case FundingPolicyType.jointCareMatters:
        aiPromptType = 'underwriting-nw-partner-guidance';
        break;
      case FundingPolicyType.assetCare:
        aiPromptType = 'underwriting-oa-guidance';
        break;
      case FundingPolicyType.jointAssetCare:
        aiPromptType = 'underwriting-oa-partner-guidance';
        break;
      case FundingPolicyType.careScoutCareAssurance:
        aiPromptType = 'underwriting-cs-guidance'
        break
      case FundingPolicyType.moneyGuardFixedAdvantage:
        aiPromptType = 'underwriting-lincoln-guidance'
        break
      default:
        // Keep default value
        break;
    }

    // Use forceRefresh prop or shouldForceRefresh state to determine if we should skip search
    const shouldSkipSearch = forceRefresh || shouldForceRefresh;

    if (shouldSkipSearch) {
      // Reset the force refresh flag since we're acting on it
      if (shouldForceRefresh) {
        setShouldForceRefresh(false);
      }
      createNewCompletion(aiPromptType, dataToUse);
    } else {
      // Search for existing completion first
      concurrentSearch.startSearch(
        {
          requesterId: clientId,
          aiPromptType: aiPromptType,
        },
        {
          onSuccess: data => {
            setSearchResult(data);
          },
          onError: () => {
            // No existing completion found, create new one
            createNewCompletion(aiPromptType, dataToUse);
          },
        },
      );
    }
  };

  const createNewCompletion = (aiPromptType: string, dataToUse: any) => {
    createCompletion('', {
      model: 'o3',
      clientId: clientId,
      aiPromptType: aiPromptType,
      metadata: { ...dataToUse },
    });
  };

  useEffect(() => {
    if (isOpen && clientId && !hasFetchedTypeform) {
      // Fetch typeform data when modal opens for the first time
      dispatch(postAiTypeformPayloadByClientIdRequest({ clientId, target: 'client' }));
      setHasFetchedTypeform(true);

      // For joint policies, also fetch partner's intake form if partner exists
      const isJointPolicy =
        formData?.policyType &&
        policyStepConfigs[formData.policyType as FundingPolicyType]?.isJoint;
      if (isJointPolicy && clientPartner?.clientId) {
        logInfo('AiUnderwritingGuidance', 'Fetching partner typeform data', {
          partnerClientId: clientPartner.clientId,
          policyType: formData.policyType,
        });
        dispatch(
          postAiTypeformPayloadByClientIdRequest({
            clientId: clientPartner.clientId,
            target: 'partner',
          }),
        );
      }
    }
  }, [isOpen, clientId, hasFetchedTypeform, dispatch, formData?.policyType, clientPartner]);

  // Trigger AI underwriting when all data is ready
  useEffect(() => {
    if (!isOpen || !clientId || isTypeformLoading || isPartnerTypeformLoading) return;

    const isJointPolicy =
      formData?.policyType && policyStepConfigs[formData.policyType as FundingPolicyType]?.isJoint;
    const needsPartnerData = isJointPolicy && clientPartner?.clientId;

    // Check if we have the required data: client data always, partner data only if needed
    const hasRequiredData = typeformPayload && (!needsPartnerData || partnerTypeformPayload);
    const canProceedWithError = !needsPartnerData && typeformError;

    if (hasRequiredData || canProceedWithError) {
      handleGetUnderwritingGuidance();
    }
  }, [
    isOpen,
    clientId,
    isTypeformLoading,
    isPartnerTypeformLoading,
    typeformPayload,
    partnerTypeformPayload,
    typeformError,
    formData?.policyType,
    clientPartner,
  ]);

  // Clean up typeform state when modal closes
  useEffect(() => {
    if (!isOpen && hasFetchedTypeform) {
      dispatch(resetAiUnderwritingTypeformState());
      setHasFetchedTypeform(false);
    }
  }, [isOpen, hasFetchedTypeform, dispatch]);

  // Use search result if available, otherwise use completion result
  const currentResult = searchResult || result;
  const parsedResults = currentResult?.results
    ? parseUnderwritingGuidanceResponse(currentResult.results)
    : null;

  return (
    <div
      className={`mb-2 flex ${disableAbsolutePosition ? '' : 'lg:absolute lg:right-0'} justify-start md:justify-center`}
    >
      <Button
        onClick={() => setIsOpen(true)}
        variant={buttonVariant}
        size={buttonSize}
        className={`flex items-center gap-2 ${className}`}
      >
        <Sparkles className="h-4 w-4" />
        {buttonText}
      </Button>

      <SheetModal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        title="Initial AI Underwriting Preview"
        subTitle="AI-powered analysis of underwriting factors"
        modal={true}
        className="sm:min-w-[55%] md:min-w-[550px] lg:min-w-[500px]"
      >
        <div className="flex min-h-full flex-1 flex-col overflow-auto">
          <div
            className={`flex flex-1 flex-col py-4 ${currentResult?.results ? '' : 'justify-center'}`}
          >
            {/* Loading States */}
            {(isTypeformLoading ||
              isPartnerTypeformLoading ||
              concurrentSearch.isSearching ||
              status === COMPLETION_STATUS.CREATING ||
              status === COMPLETION_STATUS.POLLING) &&
              !parsedResults && (
                <div className="space-y-4 text-center">
                  <Loader2 className="text-primary-dark mx-auto h-8 w-8 animate-spin" />
                  <p className="font-medium text-gray-700">
                    {concurrentSearch.isSearching
                      ? 'Searching for existing insights...'
                      : 'Getting insights...'}
                  </p>
                  <p className="text-sm text-gray-500">This may take a few moments</p>
                </div>
              )}

            {/* Error State */}
            {status === COMPLETION_STATUS.ERROR && error && (
              <div className="space-y-4 text-center">
                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <div className="flex items-center justify-center space-x-2">
                    <CircleX className="h-5 w-5 text-red-400" />
                    <h3 className="text-sm font-medium text-red-800">Analysis Error</h3>
                  </div>
                  <div className="mt-2 text-sm text-red-700">
                    <p>{error}</p>
                  </div>
                  <div className="mt-4">
                    <Button
                      onClick={handleGetUnderwritingGuidance}
                      variant="outline"
                      size="sm"
                      className="border-red-300 text-red-700 hover:bg-red-50"
                    >
                      Try Again
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Results Display */}
            {parsedResults && (
              <AiUnderwritingGuidanceResultComponent parsedResults={parsedResults} />
            )}
          </div>
        </div>
      </SheetModal>
    </div>
  );
}
